/**
 * UN/ECE Recommendation 20 unit codes. EN 16931 requires a coded unit (BT-130) on every invoice
 * line — a free-text "Stk" is not valid in an XRechnung, so FORQ's units have to be translated.
 *
 * Anything unmapped falls back to C62 (one/piece), which is the neutral code for "a countable
 * thing". That fallback is reported by the validator rather than applied silently: a wrong unit
 * on a legally mandated invoice is a defect, not a detail.
 */
export const unitCodeMap: Readonly<Record<string, string>> = {
  // Stück / pieces
  stk: 'H87',
  stück: 'H87',
  stueck: 'H87',
  st: 'H87',
  piece: 'H87',
  pcs: 'H87',
  // Zeit
  std: 'HUR',
  stunde: 'HUR',
  stunden: 'HUR',
  h: 'HUR',
  hour: 'HUR',
  tag: 'DAY',
  tage: 'DAY',
  day: 'DAY',
  woche: 'WEE',
  monat: 'MON',
  // Strecke / Fläche / Volumen
  km: 'KMT',
  m: 'MTR',
  meter: 'MTR',
  m2: 'MTK',
  qm: 'MTK',
  m3: 'MTQ',
  // Gewicht
  kg: 'KGM',
  t: 'TNE',
  to: 'TNE',
  // Menge
  l: 'LTR',
  liter: 'LTR',
  paar: 'PR',
  satz: 'SET',
  set: 'SET',
  pauschale: 'C62',
  psch: 'C62',
};

/** The neutral fallback — "one countable unit". */
export const defaultUnitCode = 'C62';

function normalise(unit: string): string {
  return unit.trim().toLowerCase().replace(/\./g, '');
}

/** The UN/ECE code for a FORQ unit, or null when there is no mapping. */
export function unitCodeFor(unit: string): string | null {
  return unitCodeMap[normalise(unit)] ?? null;
}

/** The code to write, falling back to C62. Use `unitCodeFor` when you need to know it was a guess. */
export function unitCodeOrDefault(unit: string): string {
  return unitCodeFor(unit) ?? defaultUnitCode;
}
